import { useState, useEffect, useMemo, useRef } from "react";
import html2pdf from "html2pdf.js";
import SalarySlipView from "@/components/attendance/SalarySlipView";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Wallet, Calculator, FileText, Download, CheckCircle,
  Clock, XCircle, IndianRupee, User, Eye,
  AlertCircle, History, CalendarCheck, Save,
  Search, TrendingUp, TrendingDown, Users, Briefcase,
  FileSpreadsheet, RefreshCw, MoreHorizontal, Home, Building2
} from "lucide-react";
import { useBusiness } from "@/contexts/BusinessContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================================================
// Presentational helpers — kept in this file so the page stays self-contained.
// ============================================================================

const KPI_ACCENTS = {
  emerald: "from-emerald-50 to-emerald-100 dark:from-emerald-900/30 dark:to-emerald-900/10 text-emerald-700 dark:text-emerald-300 ring-emerald-200/60 dark:ring-emerald-800/60",
  green:   "from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-900/10 text-green-700 dark:text-green-300 ring-green-200/60 dark:ring-green-800/60",
  sky:     "from-sky-50 to-sky-100 dark:from-sky-900/30 dark:to-sky-900/10 text-sky-700 dark:text-sky-300 ring-sky-200/60 dark:ring-sky-800/60",
  amber:   "from-amber-50 to-amber-100 dark:from-amber-900/30 dark:to-amber-900/10 text-amber-700 dark:text-amber-300 ring-amber-200/60 dark:ring-amber-800/60",
  rose:    "from-rose-50 to-rose-100 dark:from-rose-900/30 dark:to-rose-900/10 text-rose-700 dark:text-rose-300 ring-rose-200/60 dark:ring-rose-800/60",
  violet:  "from-violet-50 to-violet-100 dark:from-violet-900/30 dark:to-violet-900/10 text-violet-700 dark:text-violet-300 ring-violet-200/60 dark:ring-violet-800/60",
};
const KpiCard = ({ icon, accent = "emerald", label, value, hint }) => (
  <div className="relative rounded-xl bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-slate-800 p-4 hover:shadow-sm transition-shadow">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg ring-1 bg-gradient-to-br flex items-center justify-center shrink-0 ${KPI_ACCENTS[accent] || KPI_ACCENTS.emerald}`}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{label}</p>
        <p className="text-xl font-bold text-slate-800 dark:text-white truncate" title={String(value)}>{value}</p>
      </div>
    </div>
    {hint && <p className="text-xs text-slate-400 dark:text-slate-500 mt-2 line-clamp-1">{hint}</p>}
  </div>
);

const WorkModeBadge = ({ mode }) => {
  if (!mode) return null;
  const m = String(mode).toLowerCase();
  if (m === "wfh") {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300">
        <Home className="w-3 h-3" /> WFH
      </span>
    );
  }
  if (m === "field") {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
        Field
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
      <Building2 className="w-3 h-3" /> Office
    </span>
  );
};

// Color the inline status Select trigger by its current value so admins can
// scan the table at a glance — green = paid, sky = processed/unpaid, amber = hold.
const STATUS_TRIGGER_STYLES = {
  unpaid: "bg-sky-50 border-sky-200 text-sky-700 hover:bg-sky-100 dark:bg-sky-900/30 dark:border-sky-800 dark:text-sky-300",
  paid:   "bg-green-50 border-green-200 text-green-700 hover:bg-green-100 dark:bg-green-900/30 dark:border-green-800 dark:text-green-300",
  hold:   "bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100 dark:bg-amber-900/30 dark:border-amber-800 dark:text-amber-300",
};

const SlipStatusBadge = ({ status, paymentStatus }) => {
  const s = (paymentStatus === "paid") ? "paid" : (status || "draft");
  const map = {
    draft:     { bg: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300", label: "Draft", icon: <FileText className="w-3 h-3" /> },
    processed: { bg: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300",       label: "Processed", icon: <CheckCircle className="w-3 h-3" /> },
    paid:      { bg: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400", label: "Paid", icon: <CheckCircle className="w-3 h-3" /> },
    hold:      { bg: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400", label: "Hold", icon: <Clock className="w-3 h-3" /> },
  };
  const it = map[s] || map.draft;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${it.bg}`}>
      {it.icon}{it.label}
    </span>
  );
};

const SalaryManagement = () => {
  const { selectedBusinessId } = useBusiness();
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState([]);
  const [slips, setSlips] = useState([]);
  const [userRole, setUserRole] = useState("");
  // Payroll dashboard (overview = KPIs + per-employee rows from /payroll/overview)
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [showAuditDialog, setShowAuditDialog] = useState(false);

  // Filters
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedStatus, setSelectedStatus] = useState("all");
  
  // Dialogs
  const [showCalculateDialog, setShowCalculateDialog] = useState(false);
  const [showSlipDialog, setShowSlipDialog] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [calculatedSalary, setCalculatedSalary] = useState(null);
  const [selectedSlip, setSelectedSlip] = useState(null);
  // Data for the redesigned Zoho-style slip: business letterhead + user profile
  const [businessSettings, setBusinessSettings] = useState(null);
  const [slipUser, setSlipUser] = useState(null);
  const [ytdSlips, setYtdSlips] = useState(null);
  const slipRef = useRef(null);
  const [exportingSlip, setExportingSlip] = useState(false);
  const [manualAdjustment, setManualAdjustment] = useState(0);
  const [advanceDeduction, setAdvanceDeduction] = useState(0);
  const [adjustFullAdvance, setAdjustFullAdvance] = useState(true);
  const [adjustmentNotes, setAdjustmentNotes] = useState("");
  const [generating, setGenerating] = useState(false);
  
  // Credit Date Management
  const [editingCreditDate, setEditingCreditDate] = useState(null); // slip_id being edited
  const [creditDateValue, setCreditDateValue] = useState("");
  const [savingCreditDate, setSavingCreditDate] = useState(false);
  const [creditAuditLogs, setCreditAuditLogs] = useState([]);

  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  useEffect(() => {
    fetchUserRole();
    fetchStaff();
  }, [selectedBusinessId]);

  useEffect(() => {
    fetchSlips();
  }, [selectedMonth, selectedYear, selectedStatus, selectedBusinessId]);

  const fetchUserRole = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setUserRole(data.role);
      }
    } catch (error) {
      console.error("Error fetching user role:", error);
    }
  };

  const fetchStaff = async () => {
    try {
      const url = selectedBusinessId
        ? `${BACKEND_URL}/api/attendance/staff?business_id=${selectedBusinessId}`
        : `${BACKEND_URL}/api/attendance/staff`;
      
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setStaff(data);
      }
    } catch (error) {
      console.error("Error fetching staff:", error);
    }
  };

  const fetchSlips = async () => {
    try {
      setLoading(true);
      let url = `${BACKEND_URL}/api/attendance/salary/slips?month=${selectedMonth}&year=${selectedYear}`;
      
      if (selectedBusinessId) {
        url += `&business_id=${selectedBusinessId}`;
      }
      if (selectedStatus !== "all") {
        url += `&status=${selectedStatus}`;
      }
      
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setSlips(data);
      }
    } catch (error) {
      console.error("Error fetching slips:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch credit date audit logs
  const fetchCreditAuditLogs = async () => {
    try {
      let url = `${BACKEND_URL}/api/attendance/salary/credit-audit-logs?month=${selectedMonth}&year=${selectedYear}`;
      if (selectedBusinessId) {
        url += `&business_id=${selectedBusinessId}`;
      }
      
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setCreditAuditLogs(data);
      }
    } catch (error) {
      console.error("Error fetching credit audit logs:", error);
    }
  };

  // Fetch payroll overview (KPI cards + processing roster).
  // This is the single source of truth for the redesigned dashboard.
  const fetchOverview = async () => {
    try {
      setOverviewLoading(true);
      let url = `${BACKEND_URL}/api/attendance/payroll/overview?month=${selectedMonth}&year=${selectedYear}`;
      if (selectedBusinessId) url += `&business_id=${selectedBusinessId}`;
      const r = await fetch(url, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setOverview(d);
      } else {
        setOverview(null);
      }
    } catch (e) {
      console.error("payroll overview error", e);
      setOverview(null);
    } finally {
      setOverviewLoading(false);
    }
  };

  useEffect(() => {
    fetchOverview();
  }, [selectedMonth, selectedYear, selectedBusinessId]);

  // Fetch audit logs lazily when the Audit Logs dialog is opened.
  useEffect(() => {
    if (showAuditDialog) fetchCreditAuditLogs();
  }, [showAuditDialog, selectedMonth, selectedYear, selectedBusinessId]);

  const canManageSalary = ["super_admin", "admin"].includes(userRole);
  const canUpdateCreditDate = ["super_admin", "admin", "manager"].includes(userRole);

  // Handle credit date update
  const handleCreditDateUpdate = async (slipId) => {
    if (!creditDateValue && editingCreditDate === slipId) {
      // If clearing date, send null
      saveCreditDate(slipId, null);
      return;
    }
    
    if (creditDateValue) {
      saveCreditDate(slipId, creditDateValue);
    }
  };

  const saveCreditDate = async (slipId, creditDate) => {
    setSavingCreditDate(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/attendance/salary/slips/${slipId}/credit-date`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ credit_date: creditDate })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setEditingCreditDate(null);
        setCreditDateValue("");
        fetchSlips();
        fetchOverview();
        if (showAuditDialog) fetchCreditAuditLogs();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update credit date");
      }
    } catch (error) {
      toast.error("Error updating credit date");
    } finally {
      setSavingCreditDate(false);
    }
  };

  const startEditingCreditDate = (slip) => {
    setEditingCreditDate(slip.slip_id);
    setCreditDateValue(slip.salary_credit_date ? slip.salary_credit_date.split("T")[0] : "");
  };

  const togglePaymentStatus = async (slip) => {
    const current = slip.payment_status || "unpaid";
    const newStatus = current === "paid" ? "unpaid" : "paid";
    try {
      const response = await fetch(`${BACKEND_URL}/api/attendance/salary/slips/${slip.slip_id}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status: newStatus }),
      });
      if (response.ok) {
        toast.success(`Marked as ${newStatus === "paid" ? "Paid" : "Unpaid"}`);
        fetchSlips();
        fetchOverview();
        if (showAuditDialog) fetchCreditAuditLogs();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update status");
      }
    } catch (e) {
      toast.error("Error updating status");
    }
  };

  const cancelEditingCreditDate = () => {
    setEditingCreditDate(null);
    setCreditDateValue("");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return dateStr;
    }
  };

  const calculateSalary = async (staffMember) => {
    setSelectedStaff(staffMember);
    setCalculatedSalary(null);
    setShowCalculateDialog(true);

    try {
      let url = `${BACKEND_URL}/api/attendance/salary/calculate?user_id=${staffMember.user_id}&month=${selectedMonth}&year=${selectedYear}`;
      if (selectedBusinessId) {
        url += `&business_id=${selectedBusinessId}`;
      }
      
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setCalculatedSalary(data);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to calculate salary");
      }
    } catch (error) {
      toast.error("Error calculating salary");
    }
  };

  const generateSlip = async () => {
    if (!selectedStaff || !calculatedSalary) return;

    setGenerating(true);
    try {
      const advToSend = adjustFullAdvance ? (calculatedSalary.pending_advance || 0) : (parseFloat(advanceDeduction) || 0);
      let url = `${BACKEND_URL}/api/attendance/salary/generate?user_id=${selectedStaff.user_id}&month=${selectedMonth}&year=${selectedYear}&manual_adjustment=${manualAdjustment}&advance_deduction=${advToSend}&adjustment_notes=${encodeURIComponent(adjustmentNotes)}`;
      if (selectedBusinessId) {
        url += `&business_id=${selectedBusinessId}`;
      }
      
      const response = await fetch(url, {
        method: "POST",
        credentials: "include"
      });

      if (response.ok) {
        toast.success("Salary slip generated successfully");
        setShowCalculateDialog(false);
        setManualAdjustment(0);
        setAdjustmentNotes("");
        setAdvanceDeduction(0);
        setAdjustFullAdvance(true);
        fetchSlips();
        fetchOverview();
        window.dispatchEvent(new CustomEvent("hrms:data-changed", { detail: { kind: "salary" } }));
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to generate slip");
      }
    } catch (error) {
      toast.error("Error generating salary slip");
    } finally {
      setGenerating(false);
    }
  };

  const updateSlipStatus = async (slipId, status) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/attendance/salary/slips/${slipId}/status?status=${status}`,
        {
          method: "PUT",
          credentials: "include"
        }
      );

      if (response.ok) {
        toast.success(`Marked as ${status}`);
        fetchSlips();
      } else {
        toast.error("Failed to update status");
      }
    } catch (error) {
      toast.error("Error updating status");
    }
  };

  // Inline status change from the redesigned processing table. Mirrors
  // updateSlipStatus but also refreshes the payroll overview (KPI totals
  // depend on processed/pending counts) and audit logs if that dialog is open.
  const changeSlipStatus = async (slipId, newStatus) => {
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/attendance/salary/slips/${slipId}/status?status=${newStatus}`,
        { method: "PUT", credentials: "include" }
      );
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Failed to update status");
        return;
      }
      const label = newStatus === "unpaid" ? "Processed"
        : newStatus.charAt(0).toUpperCase() + newStatus.slice(1);
      toast.success(`Marked as ${label}`);
      fetchSlips();
      fetchOverview();
      if (showAuditDialog) fetchCreditAuditLogs();
    } catch (e) {
      toast.error("Error updating status");
    }
  };

  const viewSlip = async (slip) => {
    setSelectedSlip(slip);
    setShowSlipDialog(true);
    // Fetch business letterhead + user payroll profile + YTD context
    try {
      const [bizRes, userRes, ytdRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/billing/settings${selectedBusinessId ? `?business_id=${selectedBusinessId}` : ""}`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/users/${slip.user_id}`, { credentials: "include" }),
        fetch(`${BACKEND_URL}/api/attendance/salary-slips?user_id=${slip.user_id}&year=${slip.year}`, { credentials: "include" }),
      ]);
      if (bizRes.ok) {
        // GET returns the settings object directly (only PUT nests it under .settings)
        const bizData = await bizRes.json();
        setBusinessSettings(bizData?.settings || bizData || null);
      }
      if (userRes.ok) setSlipUser(await userRes.json());
      if (ytdRes.ok) {
        const data = await ytdRes.json();
        const arr = Array.isArray(data) ? data : (data.slips || []);
        // Only prior months in same year (exclude the current slip to avoid double-counting)
        const prior = arr.filter(s => s.month < slip.month && s.year === slip.year);
        setYtdSlips(prior);
      }
    } catch (_) {}
  };

  const exportSlipPdf = async () => {
    if (!slipRef.current || !selectedSlip) return;
    setExportingSlip(true);
    try {
      const filename = `Payslip-${(slipUser?.name || selectedSlip.user_name || "employee").replace(/\s+/g, "_")}-${selectedSlip.month}-${selectedSlip.year}.pdf`;
      await html2pdf().set({
        margin: 0,
        filename,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: "pt", format: "a4", orientation: "portrait" },
      }).from(slipRef.current).save();
    } finally { setExportingSlip(false); }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // ===========================================================================
  // Personal salary view (non-admin) — own KPIs + own slip history for the year.
  // Backend RBAC enforces scope; this just renders the data the API hands back.
  // ===========================================================================
  const [mySummary, setMySummary] = useState(null);
  const [myYear, setMyYear] = useState(new Date().getFullYear());
  const [myYearSlips, setMyYearSlips] = useState([]);
  const [myLoading, setMyLoading] = useState(false);

  const fetchMySalary = async () => {
    if (canManageSalary) return; // not used for admins
    try {
      setMyLoading(true);
      const m = new Date().getMonth() + 1;
      const y = new Date().getFullYear();
      // Current month KPIs
      const sumUrl = `${BACKEND_URL}/api/attendance/my-salary-preview?month=${m}&year=${y}` +
        (selectedBusinessId ? `&business_id=${selectedBusinessId}` : "");
      const sumR = await fetch(sumUrl, { credentials: "include" });
      if (sumR.ok) setMySummary(await sumR.json());
      else setMySummary(null);

      // Year-long slip history (server scopes to self via RBAC patch + month=all)
      const slipsUrl = `${BACKEND_URL}/api/attendance/salary/slips?month=all&year=${myYear}` +
        (selectedBusinessId ? `&business_id=${selectedBusinessId}` : "");
      const slipsR = await fetch(slipsUrl, { credentials: "include" });
      if (slipsR.ok) {
        const rows = await slipsR.json();
        rows.sort((a, b) => (b.month || 0) - (a.month || 0));
        setMyYearSlips(rows);
      } else {
        setMyYearSlips([]);
      }
    } catch (e) {
      console.error("personal salary fetch failed", e);
    } finally {
      setMyLoading(false);
    }
  };

  useEffect(() => {
    if (userRole && !canManageSalary) fetchMySalary();
  }, [userRole, canManageSalary, myYear, selectedBusinessId]);

  // -------- derived helpers for the payroll dashboard --------
  const monthLabel = (m) => (months.find((x) => x.value === m)?.label || "—");

  // Index existing slips by user_id for fast lookup inside the roster
  const slipByUser = useMemo(() => {
    const map = {};
    for (const s of (slips || [])) { if (s.user_id) map[s.user_id] = s; }
    return map;
  }, [slips]);

  // Apply search + role + status filters to overview rows
  const filteredRows = useMemo(() => {
    const rows = overview?.rows || [];
    const q = (searchQuery || "").trim().toLowerCase();
    return rows.filter((r) => {
      if (roleFilter !== "all" && r.role !== roleFilter) return false;
      if (selectedStatus !== "all" && r.slip_status !== selectedStatus) return false;
      if (!q) return true;
      return ((r.name || "").toLowerCase().includes(q) ||
              (r.email || "").toLowerCase().includes(q) ||
              (r.designation || "").toLowerCase().includes(q));
    });
  }, [overview, searchQuery, roleFilter, selectedStatus]);

  // CSV export of the currently-visible roster
  const exportPayrollCsv = () => {
    const rows = filteredRows;
    if (!rows.length) { toast.error("Nothing to export"); return; }
    const headers = ["Employee", "Email", "Designation", "Work Mode", "Gross Salary", "Working Days",
      "Present", "Half Days", "Leave", "LOP", "OT Hours", "Total Deductions", "Net Salary", "Status"];
    const csv = [headers.join(",")];
    for (const r of rows) {
      const cells = [
        r.name, r.email || "", r.designation, r.work_mode || "office",
        r.gross_salary, r.working_days, r.present_days, r.half_days,
        r.leave_days || 0, r.lop_days || 0, r.overtime_hours || 0,
        r.total_deductions, r.net_payable, r.slip_status,
      ].map((v) => {
        const s = String(v == null ? "" : v);
        return s.includes(",") || s.includes("\"") ? `"${s.replace(/"/g, '""')}"` : s;
      });
      csv.push(cells.join(","));
    }
    const blob = new Blob([csv.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `payroll-${selectedYear}-${String(selectedMonth).padStart(2,"0")}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} rows`);
  };

  // Salary Register = the same CSV but server-side fields. For MVP we reuse the same CSV with a banner row.
  const downloadSalaryRegister = () => {
    const banner = `Salary Register — ${monthLabel(selectedMonth)} ${selectedYear}`;
    const headers = ["Employee", "Designation", "Gross", "Net", "Status"];
    const rows = (overview?.rows || []);
    const csv = [banner, headers.join(",")];
    for (const r of rows) {
      csv.push([r.name, r.designation, r.gross_salary, r.net_payable, r.slip_status].join(","));
    }
    const blob = new Blob([csv.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `salary-register-${selectedYear}-${String(selectedMonth).padStart(2,"0")}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  // Bulk "Process Salary" — open the calculate dialog for the first pending employee.
  // (Phased: full silent batch generation would benefit from a server-side bulk endpoint.)
  const processPendingPayroll = () => {
    const pending = (overview?.rows || []).filter((r) => !r.slip_generated);
    if (!pending.length) { toast.info("All employees processed"); return; }
    const next = pending[0];
    calculateSalary({ user_id: next.user_id, name: next.name, email: next.email });
    if (pending.length > 1) {
      toast.info(`${pending.length} pending — generate them one at a time from the Action column`);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "paid":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
            <CheckCircle className="w-3 h-3" />
            Paid
          </span>
        );
      case "hold":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
            <Clock className="w-3 h-3" />
            Hold
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
            <XCircle className="w-3 h-3" />
            Unpaid
          </span>
        );
    }
  };

  // ---------- Non-admin: render the personal salary dashboard ----------
  if (userRole && !canManageSalary) {
    const status = mySummary?.payment_status || "draft";
    const currentSlip = (myYearSlips || []).find(
      (s) => s.month === (mySummary?.month) && s.year === (mySummary?.year)
    );
    return (
      <div className="p-4 md:p-6 space-y-6" data-testid="my-salary-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
              <Wallet className="w-8 h-8 text-emerald-600" />
              My Salary
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              {mySummary
                ? `${monthLabel(mySummary.month)} ${mySummary.year} — your personal payroll summary.`
                : "Your personal payroll summary."}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchMySalary} disabled={myLoading} data-testid="refresh-my-salary">
            <RefreshCw className={`w-4 h-4 mr-2 ${myLoading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {/* KPI cards (My Salary Summary) */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <KpiCard
            icon={<IndianRupee className="w-5 h-5" />}
            accent="emerald"
            label="Gross Salary"
            value={mySummary ? formatCurrency(mySummary.gross_salary) : "…"}
            hint="Basic + HRA + allowances"
          />
          <KpiCard
            icon={<Wallet className="w-5 h-5" />}
            accent="green"
            label="Net Salary"
            value={mySummary ? formatCurrency(mySummary.net_payable) : "…"}
            hint="Take-home this month"
          />
          <KpiCard
            icon={<CheckCircle className="w-5 h-5" />}
            accent="sky"
            label="Status"
            value={currentSlip ? (currentSlip.payment_status === "paid" ? "Paid" : (currentSlip.payment_status === "hold" ? "Hold" : "Processed")) : "Draft"}
            hint={currentSlip?.salary_credit_date ? `Credited: ${formatDate(currentSlip.salary_credit_date)}` : "Not yet credited"}
          />
          <KpiCard
            icon={<TrendingUp className="w-5 h-5" />}
            accent="violet"
            label="Overtime"
            value={mySummary
              ? (mySummary.overtime_eligible
                  ? `${formatCurrency(mySummary.overtime_amount)} (${mySummary.overtime_hours || 0}h)`
                  : "N/A")
              : "…"}
            hint={mySummary?.overtime_eligible === false ? "WFH — not eligible" : "Approved OT this month"}
          />
          <KpiCard
            icon={<TrendingDown className="w-5 h-5" />}
            accent="amber"
            label="Leave Deduction"
            value={mySummary ? `${mySummary.leave_days || 0} day(s)` : "…"}
            hint="Approved leave consumed"
          />
          <KpiCard
            icon={<AlertCircle className="w-5 h-5" />}
            accent="rose"
            label="LOP"
            value={mySummary ? `${mySummary.chargeable_absent_days || 0} day(s)` : "…"}
            hint="Unpaid absent days (Loss of Pay)"
          />
        </div>

        {/* My Payroll History */}
        <Card className="border-slate-200 dark:border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <History className="w-5 h-5 text-emerald-600" />
              My Salary Slips
            </CardTitle>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-500">Year</Label>
              <Select value={myYear.toString()} onValueChange={(v) => setMyYear(parseInt(v))}>
                <SelectTrigger className="h-8 w-[90px]" data-testid="my-year-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {years.map((y) => (<SelectItem key={y} value={y.toString()}>{y}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {myLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
              </div>
            ) : myYearSlips.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                <FileText className="w-12 h-12 mb-4 opacity-40" />
                <p className="font-medium">No salary slips for {myYear} yet</p>
                <p className="text-sm mt-1">Slips appear here as payroll is processed each month.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 dark:bg-slate-800">
                      <TableHead className="font-semibold">Month</TableHead>
                      <TableHead className="font-semibold text-right">Gross Salary</TableHead>
                      <TableHead className="font-semibold text-right">Deductions</TableHead>
                      <TableHead className="font-semibold text-right">Net Salary</TableHead>
                      <TableHead className="font-semibold">Status</TableHead>
                      <TableHead className="font-semibold text-right">View / Download</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {myYearSlips.map((slip) => (
                      <TableRow key={slip.slip_id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                        <TableCell className="font-medium">{monthLabel(slip.month)} {slip.year}</TableCell>
                        <TableCell className="text-right">{formatCurrency(slip.gross_salary)}</TableCell>
                        <TableCell className="text-right text-rose-600">-{formatCurrency(slip.total_deductions)}</TableCell>
                        <TableCell className="text-right font-bold text-emerald-600">{formatCurrency(slip.net_payable)}</TableCell>
                        <TableCell>{getStatusBadge(slip.payment_status)}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => viewSlip(slip)} title="View slip" data-testid={`my-view-${slip.slip_id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Reuse the existing View Slip Dialog (with print/download from its own footer) */}
        <Dialog open={showSlipDialog} onOpenChange={setShowSlipDialog}>
          <DialogContent className="max-w-[900px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-600" />
                Salary Slip — {selectedSlip ? `${monthLabel(selectedSlip.month)} ${selectedSlip.year}` : ""}
              </DialogTitle>
              <DialogDescription>Your salary slip for the selected month.</DialogDescription>
            </DialogHeader>
            {selectedSlip && (
              <div className="py-2">
                <div className="overflow-auto bg-slate-100 dark:bg-slate-950 p-3 rounded-lg">
                  <div ref={slipRef} style={{ margin: "0 auto" }}>
                    <SalarySlipView
                      slip={selectedSlip}
                      user={slipUser || { name: selectedSlip.user_name }}
                      business={businessSettings ? { name: businessSettings.business_name, address: businessSettings.business_address, city: businessSettings.business_city, state: businessSettings.business_state, pincode: businessSettings.business_pincode, phone: businessSettings.business_phone, logo_url: businessSettings.logo_url } : {}}
                      ytdSlips={ytdSlips}
                    />
                  </div>
                </div>
              </div>
            )}
            <DialogFooter className="flex flex-row justify-between gap-2">
              <Button onClick={exportSlipPdf} disabled={!selectedSlip || exportingSlip} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Download className="w-4 h-4 mr-2" /> {exportingSlip ? "Rendering…" : "Download PDF"}
              </Button>
              <Button variant="outline" onClick={() => setShowSlipDialog(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="salary-management-page">

      {/* === Header === */}
      <div className="flex flex-col md:flex-row md:items-start md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <Wallet className="w-8 h-8 text-emerald-600" />
            Salary Management
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1 max-w-2xl">
            Manage payroll, salary processing, deductions, overtime, leave deductions and salary slips.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { fetchOverview(); fetchSlips(); }}
            disabled={overviewLoading}
            data-testid="refresh-payroll"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${overviewLoading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          {canManageSalary && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportPayrollCsv()}
              data-testid="export-excel"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Export Excel
            </Button>
          )}
          {canManageSalary && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadSalaryRegister()}
              data-testid="download-register"
            >
              <Download className="w-4 h-4 mr-2" />
              Salary Register
            </Button>
          )}
          {canManageSalary && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAuditDialog(true)}
              data-testid="audit-logs"
            >
              <History className="w-4 h-4 mr-2" />
              Audit Logs
            </Button>
          )}
          {canManageSalary && (
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => processPendingPayroll()}
              disabled={!overview || (overview?.totals?.employees_pending || 0) === 0}
              data-testid="process-payroll"
            >
              <Calculator className="w-4 h-4 mr-2" />
              Process Salary ({overview?.totals?.employees_pending || 0})
            </Button>
          )}
        </div>
      </div>

      {/* === KPI Grid === */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard
          icon={<IndianRupee className="w-5 h-5" />}
          accent="emerald"
          label="Gross Payroll"
          value={overviewLoading ? "…" : formatCurrency(overview?.totals?.gross_payroll || 0)}
          hint={`Total payroll for ${monthLabel(selectedMonth)} ${selectedYear}`}
        />
        <KpiCard
          icon={<Wallet className="w-5 h-5" />}
          accent="green"
          label="Net Salary"
          value={overviewLoading ? "…" : formatCurrency(overview?.totals?.net_salary || 0)}
          hint="Total payable salary"
        />
        <KpiCard
          icon={<Users className="w-5 h-5" />}
          accent="sky"
          label="Employees Processed"
          value={overviewLoading ? "…" : (overview?.totals?.employees_processed || 0)}
          hint="Employees included in payroll"
        />
        <KpiCard
          icon={<Clock className="w-5 h-5" />}
          accent="amber"
          label="Pending Salaries"
          value={overviewLoading ? "…" : (overview?.totals?.employees_pending || 0)}
          hint="Employees awaiting processing"
        />
        <KpiCard
          icon={<TrendingDown className="w-5 h-5" />}
          accent="rose"
          label="Total Deductions"
          value={overviewLoading ? "…" : formatCurrency(overview?.totals?.total_deductions || 0)}
          hint="LOP + late + statutory + other deductions"
        />
        <KpiCard
          icon={<TrendingUp className="w-5 h-5" />}
          accent="violet"
          label="Overtime Pay"
          value={overviewLoading ? "…" : formatCurrency(overview?.totals?.overtime_pay || 0)}
          hint="Total approved overtime amount"
        />
      </div>

      {/* === Filters Bar === */}
      <Card className="border-slate-200 dark:border-slate-700">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search employee by name or email…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="employee-search"
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 lg:w-auto">
              <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                <SelectTrigger data-testid="month-filter" className="min-w-[120px]"><SelectValue placeholder="Month" /></SelectTrigger>
                <SelectContent>
                  {months.map(m => (<SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger data-testid="year-filter" className="min-w-[100px]"><SelectValue placeholder="Year" /></SelectTrigger>
                <SelectContent>
                  {years.map(y => (<SelectItem key={y} value={y.toString()}>{y}</SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger data-testid="role-filter" className="min-w-[120px]"><SelectValue placeholder="Role" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="employee">Employee</SelectItem>
                </SelectContent>
              </Select>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger data-testid="status-filter" className="min-w-[120px]"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="processed">Processed</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="hold">Hold</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* === Salary Processing Table === */}
      <Card className="border-slate-200 dark:border-slate-700">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-emerald-600" />
            Salary Processing — {monthLabel(selectedMonth)} {selectedYear}
          </CardTitle>
          <div className="text-sm text-slate-500">
            {overviewLoading ? "Loading…" : `${filteredRows.length} of ${overview?.rows?.length || 0} employees`}
          </div>
        </CardHeader>
        <CardContent>
          {overviewLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <Briefcase className="w-12 h-12 mb-4 opacity-40" />
              <p className="font-medium">No employees match the current filters</p>
              <p className="text-sm mt-1">Try clearing the search or status filter.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-800">
                    <TableHead className="font-semibold">Employee</TableHead>
                    <TableHead className="font-semibold">Designation</TableHead>
                    <TableHead className="font-semibold text-right">Gross Salary</TableHead>
                    <TableHead className="font-semibold text-center">Working</TableHead>
                    <TableHead className="font-semibold text-center">Present</TableHead>
                    <TableHead className="font-semibold text-center">Leave</TableHead>
                    <TableHead className="font-semibold text-center">LOP</TableHead>
                    <TableHead className="font-semibold text-center">OT (h)</TableHead>
                    <TableHead className="font-semibold text-right">Deductions</TableHead>
                    <TableHead className="font-semibold text-right">Net Salary</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRows.map((row) => {
                    const slip = slipByUser[row.user_id];
                    return (
                      <TableRow
                        key={row.user_id}
                        className="hover:bg-slate-50 dark:hover:bg-slate-800/40"
                        data-testid={`payroll-row-${row.user_id}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center shrink-0">
                              <User className="w-4 h-4 text-emerald-600" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium truncate">{row.name}</p>
                              <p className="text-xs text-slate-500 truncate">{row.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span>{row.designation}</span>
                            <WorkModeBadge mode={row.work_mode} />
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(row.gross_salary)}</TableCell>
                        <TableCell className="text-center text-slate-600">{row.working_days}</TableCell>
                        <TableCell className="text-center text-green-600 font-medium">
                          {row.present_days}
                          {row.half_days > 0 && <span className="text-slate-400 text-xs ml-1">+{row.half_days}½</span>}
                        </TableCell>
                        <TableCell className="text-center text-teal-600">{row.leave_days || 0}</TableCell>
                        <TableCell className="text-center">
                          {row.lop_days > 0
                            ? <span className="text-red-600 font-medium">{row.lop_days}</span>
                            : <span className="text-slate-400">0</span>}
                        </TableCell>
                        <TableCell className="text-center">
                          {row.overtime_eligible ? (
                            <span className="text-blue-600 font-medium">{row.overtime_hours}h</span>
                          ) : (
                            <span className="text-slate-400 italic" title="WFH — overtime not applicable">N/A</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right text-rose-600">
                          {row.total_deductions > 0 ? "-" : ""}{formatCurrency(row.total_deductions)}
                        </TableCell>
                        <TableCell className="text-right font-bold text-emerald-600">
                          {formatCurrency(row.net_payable)}
                        </TableCell>
                        <TableCell>
                          {canManageSalary && row.slip_id ? (
                            <Select
                              value={row.payment_status || "unpaid"}
                              onValueChange={(v) => changeSlipStatus(row.slip_id, v)}
                            >
                              <SelectTrigger
                                className={`h-8 w-[130px] font-medium ${STATUS_TRIGGER_STYLES[row.payment_status || "unpaid"]}`}
                                data-testid={`status-select-${row.slip_id}`}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="unpaid">
                                  <span className="inline-flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-sky-500" /> Processed
                                  </span>
                                </SelectItem>
                                <SelectItem value="paid">
                                  <span className="inline-flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-green-500" /> Paid
                                  </span>
                                </SelectItem>
                                <SelectItem value="hold">
                                  <span className="inline-flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-amber-500" /> Hold
                                  </span>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <SlipStatusBadge status={row.slip_status} paymentStatus={row.payment_status} />
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              title={slip ? "View salary slip" : "View breakdown & generate slip"}
                              onClick={() => {
                                if (slip) viewSlip(slip);
                                else calculateSalary({ user_id: row.user_id, name: row.name, email: row.email });
                              }}
                              data-testid={`view-${row.user_id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {canManageSalary && (
                              <Button
                                variant="ghost"
                                size="sm"
                                title={slip ? "Recalculate" : "Calculate & generate"}
                                onClick={() => calculateSalary({ user_id: row.user_id, name: row.name, email: row.email })}
                                data-testid={`calc-${row.user_id}`}
                              >
                                <Calculator className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* === Audit Logs Dialog === */}
      <Dialog open={showAuditDialog} onOpenChange={setShowAuditDialog}>
        <DialogContent className="sm:max-w-[900px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-emerald-600" />
              Salary Credit Audit Log — {monthLabel(selectedMonth)} {selectedYear}
            </DialogTitle>
            <DialogDescription>
              All status & credit-date changes recorded for this payroll period.
            </DialogDescription>
          </DialogHeader>
          {creditAuditLogs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <History className="w-12 h-12 mb-4 opacity-50" />
              <p>No salary credit date changes recorded</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-800">
                    <TableHead className="font-semibold">Date &amp; Time</TableHead>
                    <TableHead className="font-semibold">Employee</TableHead>
                    <TableHead className="font-semibold">Old Credit Date</TableHead>
                    <TableHead className="font-semibold">New Credit Date</TableHead>
                    <TableHead className="font-semibold">Status Change</TableHead>
                    <TableHead className="font-semibold">Updated By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {creditAuditLogs.map((log) => (
                    <TableRow key={log.log_id}>
                      <TableCell className="text-sm text-slate-500">{formatDateTime(log.updated_at || log.created_at)}</TableCell>
                      <TableCell className="font-medium">{log.user_name}</TableCell>
                      <TableCell className="text-slate-500">{log.old_credit_date ? formatDate(log.old_credit_date) : "-"}</TableCell>
                      <TableCell className={log.new_credit_date ? "text-green-600 font-medium" : "text-red-500"}>
                        {log.new_credit_date ? formatDate(log.new_credit_date) : "Removed"}
                      </TableCell>
                      <TableCell>
                        {log.old_payment_status && log.new_payment_status ? (
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${log.old_payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}`}>{log.old_payment_status}</span>
                            <span className="text-slate-400">→</span>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${log.new_payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{log.new_payment_status}</span>
                          </div>
                        ) : <span className="text-slate-400">—</span>}
                      </TableCell>
                      <TableCell className="text-slate-500">{log.changed_by_name || log.updated_by_name || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAuditDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Calculate Salary Dialog */}
      <Dialog open={showCalculateDialog} onOpenChange={setShowCalculateDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-emerald-600" />
              Salary Calculation - {selectedStaff?.name || selectedStaff?.email}
            </DialogTitle>
            <DialogDescription>
              {months.find(m => m.value === selectedMonth)?.label} {selectedYear}
            </DialogDescription>
          </DialogHeader>
          
          {calculatedSalary ? (
            <div className="space-y-4 py-4">
              {/* Working Days Summary */}
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <h4 className="font-medium text-sm text-blue-700 dark:text-blue-300 mb-2">Working Days Breakdown</h4>
                <div className="grid grid-cols-4 gap-2 text-center text-sm">
                  <div>
                    <p className="text-lg font-bold text-slate-800 dark:text-white">{calculatedSalary.total_days_in_month || calculatedSalary.working_days}</p>
                    <p className="text-xs text-slate-500">Total Days</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-amber-600">{calculatedSalary.weekly_off_days || 0}</p>
                    <p className="text-xs text-slate-500">Weekly Offs</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-purple-600">{calculatedSalary.holiday_days || 0}</p>
                    <p className="text-xs text-slate-500">Holidays</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-emerald-600">{calculatedSalary.working_days}</p>
                    <p className="text-xs text-slate-500">Working Days</p>
                  </div>
                </div>
                {calculatedSalary.holidays_excluded?.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-blue-200 dark:border-blue-700">
                    <p className="text-xs text-blue-600 dark:text-blue-400">
                      Holidays excluded: {calculatedSalary.holidays_excluded.map(h => h.name).join(", ")}
                    </p>
                  </div>
                )}
              </div>

              {/* Attendance Summary */}
              <div className="grid grid-cols-5 gap-2">
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                  <p className="text-xl font-bold text-green-600">
                    {calculatedSalary.present_days}
                  </p>
                  <p className="text-xs text-slate-500">Present</p>
                </div>
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-center">
                  <p className="text-xl font-bold text-amber-600">
                    {calculatedSalary.half_days}
                  </p>
                  <p className="text-xs text-slate-500">Half Days</p>
                </div>
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-center">
                  <p className="text-xl font-bold text-red-600">
                    {calculatedSalary.absent_days}
                  </p>
                  <p className="text-xs text-slate-500">Absent</p>
                </div>
                <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg text-center">
                  <p className="text-xl font-bold text-orange-600">
                    {calculatedSalary.late_count}
                  </p>
                  <p className="text-xs text-slate-500">Late</p>
                </div>
                <div className="p-3 bg-teal-50 dark:bg-teal-900/20 rounded-lg text-center">
                  <p className="text-xl font-bold text-teal-600">
                    {calculatedSalary.leave_days || 0}
                  </p>
                  <p className="text-xs text-slate-500">Leaves</p>
                </div>
              </div>

              {/* Annual Leave Card — shows how absent days were absorbed before LOP */}
              {calculatedSalary.annual_leave_entitlement > 0 && (
                <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
                  <h4 className="font-medium text-sm text-emerald-700 dark:text-emerald-300 mb-2">Annual Leave</h4>
                  <div className="grid grid-cols-4 gap-2 text-center text-sm">
                    <div>
                      <p className="text-lg font-bold text-slate-800 dark:text-white">{calculatedSalary.annual_leave_entitlement}</p>
                      <p className="text-xs text-slate-500">Entitlement</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-amber-600">{calculatedSalary.annual_leave_used_ytd || 0}</p>
                      <p className="text-xs text-slate-500">Used YTD</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-emerald-600">{calculatedSalary.annual_leave_remaining || 0}</p>
                      <p className="text-xs text-slate-500">Remaining</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-blue-600">{calculatedSalary.annual_leave_absorbed_this_month || 0}</p>
                      <p className="text-xs text-slate-500">Absorbed (this mo.)</p>
                    </div>
                  </div>
                  {calculatedSalary.absent_days > 0 && (
                    <div className="mt-2 pt-2 border-t border-emerald-200 dark:border-emerald-800 text-xs text-emerald-700 dark:text-emerald-300">
                      {calculatedSalary.chargeable_absent_days > 0 ? (
                        <>Absent {calculatedSalary.absent_days} day(s) — {calculatedSalary.annual_leave_absorbed_this_month} covered by AL, <strong className="text-red-600">{calculatedSalary.chargeable_absent_days} treated as LOP</strong>.</>
                      ) : (
                        <>Absent {calculatedSalary.absent_days} day(s) — fully covered by AL balance, no salary deduction.</>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Work mode + OT eligibility — only render when backend returns it */}
              {calculatedSalary.work_mode && (
                <div className={`p-3 rounded-lg border text-sm flex items-center justify-between gap-3 ${
                  calculatedSalary.overtime_eligible === false
                    ? "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-800"
                    : "bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                }`}>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-600 dark:text-slate-400">Work Mode:</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold uppercase tracking-wide ${
                      calculatedSalary.work_mode === "wfh"
                        ? "bg-yellow-200 text-yellow-900 dark:bg-yellow-700 dark:text-yellow-100"
                        : calculatedSalary.work_mode === "field"
                        ? "bg-blue-200 text-blue-900 dark:bg-blue-700 dark:text-blue-100"
                        : "bg-emerald-200 text-emerald-900 dark:bg-emerald-700 dark:text-emerald-100"
                    }`}>
                      {calculatedSalary.work_mode === "wfh" ? "WFH" : calculatedSalary.work_mode === "field" ? "Field" : "Office"}
                    </span>
                  </div>
                  <div className="text-xs">
                    {calculatedSalary.overtime_eligible === false ? (
                      <span className="text-yellow-700 dark:text-yellow-300 font-medium">
                        Overtime is not applicable for employees working from home (WFH).
                      </span>
                    ) : (
                      <span className="text-slate-500">Overtime eligible</span>
                    )}
                  </div>
                </div>
              )}

              {/* Per Day Salary Info */}
              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-600 dark:text-slate-400">Per Day Salary</span>
                  <span className="font-semibold">{formatCurrency(calculatedSalary.per_day_salary || (calculatedSalary.gross_salary / 30))}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  (Gross Salary ÷ {calculatedSalary.calculation_settings?.salary_calculation_days || 30} days)
                </p>
              </div>

              {/* Earnings */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium text-sm text-slate-700 dark:text-slate-300 mb-3">Earnings</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Basic Salary</span>
                    <span>{formatCurrency(calculatedSalary.basic_salary)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>HRA</span>
                    <span>{formatCurrency(calculatedSalary.hra)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Conveyance</span>
                    <span>{formatCurrency(calculatedSalary.conveyance)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Other Allowances</span>
                    <span>{formatCurrency(calculatedSalary.other_allowances)}</span>
                  </div>
                  {calculatedSalary.overtime_amount > 0 && (
                    <div className="flex justify-between text-blue-600">
                      <span>Overtime ({calculatedSalary.overtime_hours}h)</span>
                      <span>+{formatCurrency(calculatedSalary.overtime_amount)}</span>
                    </div>
                  )}
                  {calculatedSalary.overtime_eligible === false && (
                    <div className="flex justify-between text-yellow-700 dark:text-yellow-400">
                      <span className="line-through opacity-60">Overtime (excluded — WFH)</span>
                      <span>₹0</span>
                    </div>
                  )}
                  <div className="flex justify-between font-medium pt-2 border-t">
                    <span>Gross Salary</span>
                    <span>{formatCurrency(calculatedSalary.gross_salary)}</span>
                  </div>
                </div>
              </div>

              {/* Deductions */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium text-sm text-slate-700 dark:text-slate-300 mb-3">Deductions</h4>
                <div className="space-y-2 text-sm">
                  {calculatedSalary.absent_deduction > 0 && (
                    <div className="flex justify-between text-red-600">
                      <span>
                        Absent Deduction ({calculatedSalary.chargeable_absent_days ?? calculatedSalary.absent_days} chargeable {((calculatedSalary.chargeable_absent_days ?? calculatedSalary.absent_days) === 1) ? "day" : "days"} × ₹{Math.round(calculatedSalary.per_day_salary || calculatedSalary.gross_salary/30)})
                      </span>
                      <span>-{formatCurrency(calculatedSalary.absent_deduction)}</span>
                    </div>
                  )}
                  {calculatedSalary.half_day_deduction > 0 && (
                    <div className="flex justify-between text-red-600">
                      <span>Half Day Deduction ({calculatedSalary.half_days} × 50%)</span>
                      <span>-{formatCurrency(calculatedSalary.half_day_deduction)}</span>
                    </div>
                  )}
                  {calculatedSalary.late_deduction > 0 && (
                    <div className="flex justify-between text-red-600">
                      <span>
                        Late Penalty ({calculatedSalary.late_count} late marks
                        {calculatedSalary.late_converted_half_days > 0 && ` = ${calculatedSalary.late_converted_half_days} half day(s)`})
                      </span>
                      <span>-{formatCurrency(calculatedSalary.late_deduction)}</span>
                    </div>
                  )}
                  {calculatedSalary.pf_deduction > 0 && (
                    <div className="flex justify-between">
                      <span>PF</span>
                      <span>-{formatCurrency(calculatedSalary.pf_deduction)}</span>
                    </div>
                  )}
                  {calculatedSalary.esi_deduction > 0 && (
                    <div className="flex justify-between">
                      <span>ESI</span>
                      <span>-{formatCurrency(calculatedSalary.esi_deduction)}</span>
                    </div>
                  )}
                  {calculatedSalary.tds_deduction > 0 && (
                    <div className="flex justify-between">
                      <span>TDS</span>
                      <span>-{formatCurrency(calculatedSalary.tds_deduction)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-medium pt-2 border-t text-red-600">
                    <span>Total Deductions</span>
                    <span>-{formatCurrency(calculatedSalary.total_deductions)}</span>
                  </div>
                </div>
              </div>

              {/* Manual Adjustment */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium text-sm text-slate-700 dark:text-slate-300 mb-3">Manual Adjustment</h4>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="adjustment">Adjustment Amount (+ or -)</Label>
                    <Input
                      id="adjustment"
                      type="number"
                      value={manualAdjustment}
                      onChange={(e) => setManualAdjustment(parseFloat(e.target.value) || 0)}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      value={adjustmentNotes}
                      onChange={(e) => setAdjustmentNotes(e.target.value)}
                      placeholder="Reason for adjustment..."
                      rows={2}
                    />
                  </div>
                </div>
              </div>

              {/* Advance Adjustment */}
              {(calculatedSalary.pending_advance || 0) > 0 && (
                <div className="border border-amber-300 rounded-lg p-4 bg-amber-50/50">
                  <h4 className="font-medium text-sm text-amber-800 mb-3 flex items-center gap-2">
                    ⚠️ Advance Adjustment
                  </h4>
                  <div className="flex items-center justify-between mb-3 text-sm">
                    <span className="text-slate-700">Total Pending Advance:</span>
                    <span className="font-bold text-amber-700">{formatCurrency(calculatedSalary.pending_advance || 0)}</span>
                  </div>
                  <div className="space-y-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={adjustFullAdvance}
                        onChange={(e) => setAdjustFullAdvance(e.target.checked)}
                        className="w-4 h-4"
                        data-testid="adjust-full-advance-checkbox"
                      />
                      <span className="text-sm">Adjust full pending advance ({formatCurrency(calculatedSalary.pending_advance || 0)})</span>
                    </label>
                    {!adjustFullAdvance && (
                      <div>
                        <Label htmlFor="advance-amount">Custom Adjustment Amount</Label>
                        <Input
                          id="advance-amount"
                          type="number"
                          value={advanceDeduction}
                          onChange={(e) => setAdvanceDeduction(parseFloat(e.target.value) || 0)}
                          max={calculatedSalary.pending_advance || 0}
                          placeholder="0"
                          data-testid="advance-deduction-input"
                        />
                        <p className="text-xs text-slate-500 mt-1">Max: {formatCurrency(calculatedSalary.pending_advance || 0)}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Net Payable */}
              <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-lg">Net Payable</span>
                  <span className="text-2xl font-bold text-emerald-600">
                    {formatCurrency(
                      calculatedSalary.net_payable
                      + manualAdjustment
                      - (adjustFullAdvance
                        ? (calculatedSalary.pending_advance || 0)
                        : (parseFloat(advanceDeduction) || 0))
                    )}
                  </span>
                </div>
                {(calculatedSalary.pending_advance || 0) > 0 && (
                  <div className="flex justify-between items-center text-xs text-amber-700 mt-1">
                    <span>Advance deducted:</span>
                    <span>-{formatCurrency(adjustFullAdvance ? (calculatedSalary.pending_advance || 0) : (parseFloat(advanceDeduction) || 0))}</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCalculateDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={generateSlip}
              disabled={!calculatedSalary || generating}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {generating ? "Generating..." : (
                <>
                  <FileText className="w-4 h-4 mr-2" />
                  Generate Slip
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Slip Dialog */}
      <Dialog open={showSlipDialog} onOpenChange={setShowSlipDialog}>
        <DialogContent className="max-w-[900px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-600" />
              Salary Slip — {selectedSlip ? `${months.find(m => m.value === selectedSlip.month)?.label} ${selectedSlip.year}` : ""}
            </DialogTitle>
          </DialogHeader>

          {selectedSlip && (
            <div className="py-2">
              <div className="overflow-auto bg-slate-100 dark:bg-slate-950 p-3 rounded-lg">
                <div ref={slipRef} style={{ margin: "0 auto" }}>
                  <SalarySlipView
                    slip={selectedSlip}
                    user={slipUser || { name: selectedSlip.user_name }}
                    business={businessSettings ? { name: businessSettings.business_name, address: businessSettings.business_address, city: businessSettings.business_city, state: businessSettings.business_state, pincode: businessSettings.business_pincode, phone: businessSettings.business_phone, logo_url: businessSettings.logo_url } : {}}
                    ytdSlips={ytdSlips}
                  />
                </div>
              </div>
              {selectedSlip.adjustment_notes && (
                <div className="mt-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-xs text-slate-500">Adjustment Notes</p>
                  <p className="text-sm">{selectedSlip.adjustment_notes}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex flex-row justify-between gap-2">
            <Button onClick={exportSlipPdf} disabled={!selectedSlip || exportingSlip} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Download className="w-4 h-4 mr-2" /> {exportingSlip ? "Rendering…" : "Download PDF"}
            </Button>
            <Button variant="outline" onClick={() => setShowSlipDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalaryManagement;
